import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { GET_CUSTOMERS } from '../graphql/queries';
import { useAuth } from '../utils/authContext';
import { Search, Loader, Eye, ChevronLeft, ChevronRight } from 'lucide-react';
import Layout from '../components/Layout';

const Customers = () => {

    const { user } = useAuth();
    const catalogId = user?.selectedCatalog?.id;

    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const ITEMS_PER_PAGE = 10;

    const { loading, error, data } = useQuery(GET_CUSTOMERS, {
        variables: {
            catalogId: catalogId,
            search,
            page,
            limit: ITEMS_PER_PAGE
        }
    });

    const totalCount = data?.getCustomers?.totalCount || 0;
    const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

    if (error) {
        return (
            <Layout>
                <div className="p-4 bg-red-50 text-red-600 rounded-md">
                    Error loading customers: {error.message}
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="p-6 pb-12">
                <div className="mb-6 flex flex-col md:flex-row justify-between">
                    <h1 className="text-2xl font-bold text-gray-800 mb-2">Customers</h1>
                    <div className="relative">
                        <input
                            type="text"
                            placeholder="Search customers..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="pl-10 pr-4 py-2 bg-white border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-64"
                        />
                        <Search className="w-5 h-5 text-gray-400 absolute left-3 top-2.5" />
                    </div>
                </div>

                {loading ? (
                    <div className="flex justify-center items-center h-64">
                        <Loader className="w-8 h-8 animate-spin text-blue-500" />
                    </div>
                ) : (
                    <>
                        <div className="overflow-x-auto bg-white rounded-lg shadow">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Purchases</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Purchase</th>
                                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {data?.getCustomers.edges.map(({ node }) => (
                                        <tr key={node.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{node.name}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{node.email}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{node.phone}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{node.totalPurchases}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {node.lastPurchase ? new Date(node.lastPurchase).toLocaleDateString() : 'Never'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <button className="text-blue-600 hover:text-blue-900 inline-flex items-center gap-1 bg-gray-100">
                                                    <Eye className="w-4 h-4" />
                                                    View
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="flex justify-between items-center mt-4">
                            <span className="text-sm text-gray-700">
                                Showing {Math.min(ITEMS_PER_PAGE * (page - 1) + 1, totalCount)} - {Math.min(ITEMS_PER_PAGE * page, totalCount)} of {totalCount} customers
                            </span>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => setPage(p => Math.max(1, p - 1))}
                                    disabled={page === 1}
                                    className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <ChevronLeft className="w-4 h-4 mr-1" />
                                    Previous
                                </button>
                                <button
                                    onClick={() => setPage(p => p + 1)}
                                    disabled={!data?.getCustomers.pageInfo.hasNextPage}
                                    className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Next
                                    <ChevronRight className="w-4 h-4 ml-1" />
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </Layout>
    );
};

export default Customers;
